import {
	type getInputProps,
	useInputControl,
	type FieldMetadata,
	type getSelectProps,
} from '@conform-to/react'
import { endOfDay, format, startOfDay } from 'date-fns'
import { REGEXP_ONLY_DIGITS_AND_CHARS, type OTPInputProps } from 'input-otp'
import React, { useId, useState } from 'react'
import { type DateRange } from 'react-day-picker'
import {
	MediaCard,
	type MediaItem,
} from '#app/routes/dashboard+/media/components/media-card.tsx'
import { cn } from '#app/utils/misc.tsx'
import { Button } from './ui/button.tsx'
import { Calendar } from './ui/calendar.tsx'
import { Checkbox, type CheckboxProps } from './ui/checkbox.tsx'
import {
	Command,
	CommandInput,
	CommandList,
	CommandEmpty,
	CommandGroup,
	CommandItem,
} from './ui/command.tsx'
import { Icon } from './ui/icon.tsx'
import {
	InputOTP,
	InputOTPGroup,
	InputOTPSeparator,
	InputOTPSlot,
} from './ui/input-otp.tsx'
import { Input } from './ui/input.tsx'
import { Label } from './ui/label.tsx'
import MultipleSelector, { type Option } from './ui/multiselect.tsx'
import { Popover, PopoverTrigger, PopoverContent } from './ui/popover.tsx'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from './ui/select.tsx'
import { Switch } from './ui/switch.tsx'
import { Textarea } from './ui/textarea.tsx'

export type ListOfErrors = Array<string | null | undefined> | null | undefined

export function ErrorList({
	id,
	errors,
}: {
	errors?: ListOfErrors
	id?: string
}) {
	const errorsToRender = errors?.filter(Boolean)
	if (!errorsToRender?.length) return null
	return (
		<ul id={id} className="flex flex-col gap-1">
			{errorsToRender.map((e) => (
				<li key={e} className="text-destructive text-[10px]">
					{e}
				</li>
			))}
		</ul>
	)
}

export function Field({
	labelProps,
	inputProps,
	errors,
	className,
}: {
	labelProps: React.LabelHTMLAttributes<HTMLLabelElement>
	inputProps: React.InputHTMLAttributes<HTMLInputElement>
	errors?: ListOfErrors
	className?: string
}) {
	const fallbackId = useId()
	const id = inputProps.id ?? fallbackId
	const errorId = errors?.length ? `${id}-error` : undefined
	return (
		<div className={className}>
			<Label htmlFor={id} {...labelProps} />
			<Input
				id={id}
				aria-invalid={errorId ? true : undefined}
				aria-describedby={errorId}
				{...inputProps}
			/>
			<div className="min-h-[32px] px-4 pt-1 pb-3">
				{errorId ? <ErrorList id={errorId} errors={errors} /> : null}
			</div>
		</div>
	)
}

export function OTPField({
	labelProps,
	inputProps,
	errors,
	className,
}: {
	labelProps: React.LabelHTMLAttributes<HTMLLabelElement>
	inputProps: Partial<OTPInputProps & { render: never }>
	errors?: ListOfErrors
	className?: string
}) {
	const fallbackId = useId()
	const id = inputProps.id ?? fallbackId
	const errorId = errors?.length ? `${id}-error` : undefined
	return (
		<div className={className}>
			<Label htmlFor={id} {...labelProps} />
			<InputOTP
				pattern={REGEXP_ONLY_DIGITS_AND_CHARS}
				maxLength={6}
				id={id}
				aria-invalid={errorId ? true : undefined}
				aria-describedby={errorId}
				{...inputProps}
			>
				<InputOTPGroup>
					<InputOTPSlot index={0} />
					<InputOTPSlot index={1} />
					<InputOTPSlot index={2} />
				</InputOTPGroup>
				<InputOTPSeparator />
				<InputOTPGroup>
					<InputOTPSlot index={3} />
					<InputOTPSlot index={4} />
					<InputOTPSlot index={5} />
				</InputOTPGroup>
			</InputOTP>
			<div className="min-h-[32px] px-4 pt-1 pb-3">
				{errorId ? <ErrorList id={errorId} errors={errors} /> : null}
			</div>
		</div>
	)
}

export function TextareaField({
	labelProps,
	textareaProps,
	errors,
	className,
}: {
	labelProps: React.LabelHTMLAttributes<HTMLLabelElement>
	textareaProps: React.TextareaHTMLAttributes<HTMLTextAreaElement>
	errors?: ListOfErrors
	className?: string
}) {
	const fallbackId = useId()
	const id = textareaProps.id ?? textareaProps.name ?? fallbackId
	const errorId = errors?.length ? `${id}-error` : undefined
	return (
		<div className={className}>
			<Label htmlFor={id} {...labelProps} />
			<Textarea
				id={id}
				aria-invalid={errorId ? true : undefined}
				aria-describedby={errorId}
				{...textareaProps}
			/>
			<div className="min-h-[32px] px-4 pt-1 pb-3">
				{errorId ? <ErrorList id={errorId} errors={errors} /> : null}
			</div>
		</div>
	)
}

export function SelectField({
	labelProps,
	selectProps,
	selectOptions,
	errors,
	className,
	field,
}: {
	labelProps: React.LabelHTMLAttributes<HTMLLabelElement>
	selectProps: ReturnType<typeof getSelectProps> & {
		key?: string
		placeholder?: string
		disabled?: boolean
		onValueChange?: (value: string) => void
	}
	selectOptions?: { value: string; label: string | React.ReactNode }[]
	errors?: ListOfErrors
	className?: string
	field: FieldMetadata<string | null>
}) {
	const fallbackId = useId()
	const { onValueChange, key, ...restSelectProps } = selectProps
	const id = restSelectProps.id ?? fallbackId
	const errorId = errors?.length ? `${id}-error` : undefined
	const placeholder = restSelectProps?.placeholder || 'Select'

	const control = useInputControl(field)

	return (
		<div className={className}>
			<Label htmlFor={id} {...labelProps} />
			<Select
				key={key}
				disabled={restSelectProps.disabled}
				value={control.value}
				onValueChange={(value) => {
					control.change(value) // Handle form control
					onValueChange?.(value) // Handle custom callback
				}}
				name={restSelectProps.name}
				form={restSelectProps.form}
			>
				<SelectTrigger
					className="w-full py-4.5"
					id={id}
					aria-invalid={errorId ? true : undefined}
					aria-describedby={errorId}
				>
					<SelectValue placeholder={placeholder} />
				</SelectTrigger>
				<SelectContent>
					{selectOptions?.map((option) =>
						option.value ? (
							<SelectItem
								key={`${id}-${option.value}`}
								value={option.value}
								className="hover:bg-primary/10"
							>
								{option.label}
							</SelectItem>
						) : null,
					)}
				</SelectContent>
			</Select>
			<div className="min-h-[32px] px-4 pt-1 pb-3">
				{errorId ? <ErrorList id={errorId} errors={errors} /> : null}
			</div>
		</div>
	)
}

export function CheckboxField({
	labelProps,
	buttonProps,
	errors,
	className,
}: {
	labelProps: React.ComponentProps<'label'>
	buttonProps: CheckboxProps & {
		name: string
		form: string
		value?: string
	}
	errors?: ListOfErrors
	className?: string
}) {
	const { key, defaultChecked, ...checkboxProps } = buttonProps
	const fallbackId = useId()
	const checkedValue = buttonProps.value ?? 'on'
	const input = useInputControl({
		key,
		name: buttonProps.name,
		formId: buttonProps.form,
		initialValue: defaultChecked ? checkedValue : undefined,
	})
	const id = buttonProps.id ?? fallbackId
	const errorId = errors?.length ? `${id}-error` : undefined

	return (
		<div className={className}>
			<div className="flex gap-2">
				<Checkbox
					{...checkboxProps}
					id={id}
					aria-invalid={errorId ? true : undefined}
					aria-describedby={errorId}
					checked={input.value === checkedValue}
					onCheckedChange={(state) => {
						input.change(state.valueOf() ? checkedValue : '')
						buttonProps.onCheckedChange?.(state)
					}}
					onFocus={(event) => {
						input.focus()
						buttonProps.onFocus?.(event)
					}}
					onBlur={(event) => {
						input.blur()
						buttonProps.onBlur?.(event)
					}}
					type="button"
				/>
				<label
					htmlFor={id}
					{...labelProps}
					className="text-muted-foreground self-center text-xs"
				/>
			</div>
			<div className="px-4 pt-1 pb-3">
				{errorId ? <ErrorList id={errorId} errors={errors} /> : null}
			</div>
		</div>
	)
}

export function ComboboxField({
	labelProps,
	buttonProps,
	comboboxProps,
	comboboxOptions,
	errors,
	className,
	field,
	disabled,
	onValueChange,
}: {
	labelProps: React.LabelHTMLAttributes<HTMLLabelElement>
	buttonProps?: React.ComponentProps<typeof Button> & {
		key?: string
	}
	comboboxProps: {
		name: string
		placeholder?: string
		emptyText?: string
	}
	comboboxOptions: {
		value: string
		label: string | React.ReactNode
		searchText?: string
	}[]
	errors?: ListOfErrors
	className?: string
	field: FieldMetadata<string | null>
	disabled?: boolean
	onValueChange?: (value: string) => void
}) {
	const { key: buttonKey, ...restButtonProps } = buttonProps || {}
	const [open, setOpen] = useState(false)
	const control = useInputControl(field)
	const fallbackId = useId()
	const id = comboboxProps.name ?? fallbackId
	const errorId = errors?.length ? `${id}-error` : undefined

	return (
		<div className={className}>
			<Label htmlFor={id} {...labelProps} />
			<Popover modal={true} open={open && !disabled} onOpenChange={setOpen}>
				<PopoverTrigger asChild>
					<Button
						disabled={disabled}
						{...restButtonProps}
						variant="outline"
						role="combobox"
						aria-expanded={open}
						aria-invalid={errorId ? true : undefined}
						aria-describedby={errorId}
						className="w-full min-w-40 justify-between"
					>
						{control.value
							? comboboxOptions.find((option) => option.value === control.value)
									?.label
							: comboboxProps.placeholder || 'Select...'}
						<Icon name="caret-sort" className="ml-2 shrink-0" />
					</Button>
				</PopoverTrigger>

				<PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0">
					<Command
						filter={(value, search) => {
							const item = comboboxOptions.find((item) => item.value === value)
							if (!item) return 0

							// If searchText exists, use it, otherwise fallback to label
							const searchableText =
								item.searchText ||
								(typeof item.label === 'string' ? item.label : '')

							if (searchableText.toLowerCase().includes(search.toLowerCase()))
								return 1
							return 0
						}}
					>
						<CommandInput placeholder="Search..." />
						<CommandList>
							<CommandEmpty>
								{comboboxProps.emptyText || 'No options found.'}
							</CommandEmpty>
							<CommandGroup>
								{comboboxOptions.map((option) => (
									<CommandItem
										key={option.value}
										value={option.value}
										onSelect={(currentValue) => {
											control.change(currentValue)
											onValueChange?.(currentValue)
											setOpen(false)
										}}
									>
										<Icon
											name="check"
											className={cn(
												'mr-2 h-4 w-4',
												control.value === option.value
													? 'opacity-100'
													: 'opacity-0',
											)}
										/>
										{option.label}
									</CommandItem>
								))}
							</CommandGroup>
						</CommandList>
					</Command>
				</PopoverContent>
			</Popover>
			<div className="min-h-[32px] px-4 pt-1 pb-3">
				{errorId ? <ErrorList id={errorId} errors={errors} /> : null}
			</div>
		</div>
	)
}

export function SwitchField({
	labelProps,
	buttonProps,
	errors,
	className,
}: {
	labelProps: React.LabelHTMLAttributes<HTMLLabelElement>
	buttonProps: ReturnType<typeof getInputProps> & {
		name: string
		form: string
		value?: string
	}
	errors?: ListOfErrors
	className?: string
}) {
	const { key, defaultChecked, type, ...switchProps } = buttonProps
	const fallbackId = useId()
	const checkedValue = buttonProps.value ?? 'on'
	const input = useInputControl({
		key,
		name: buttonProps.name,
		formId: buttonProps.form,
		initialValue: defaultChecked ? checkedValue : undefined,
	})
	const id = buttonProps.id ?? fallbackId
	const errorId = errors?.length ? `${id}-error` : undefined

	return (
		<div className={className}>
			<div className="flex gap-2">
				<Switch
					{...switchProps}
					id={id}
					aria-invalid={errorId ? true : undefined}
					aria-describedby={errorId}
					checked={input.value === checkedValue}
					onCheckedChange={(state) => {
						input.change(state ? checkedValue : '')
					}}
				/>
				<label
					htmlFor={id}
					{...labelProps}
					className="self-center text-sm font-medium"
				/>
			</div>
			<div className="px-4 pt-1 pb-3">
				{errorId ? <ErrorList id={errorId} errors={errors} /> : null}
			</div>
		</div>
	)
}

export function TimeField({
	labelProps,
	inputProps,
	errors,
	className,
}: {
	labelProps: React.LabelHTMLAttributes<HTMLLabelElement>
	inputProps: React.InputHTMLAttributes<HTMLInputElement> & {
		key?: string
	}
	errors?: ListOfErrors
	className?: string
}) {
	const fallbackId = useId()
	const { key, ...restInputProps } = inputProps
	const id = restInputProps.id ?? fallbackId
	const errorId = errors?.length ? `${id}-error` : undefined

	return (
		<div className={className}>
			<Label htmlFor={id} {...labelProps} />
			<Input
				key={key}
				id={id}
				type="time"
				aria-invalid={errorId ? true : undefined}
				aria-describedby={errorId}
				{...restInputProps}
			/>
			<div className="min-h-[32px] px-4 pt-1 pb-3">
				{errorId ? <ErrorList id={errorId} errors={errors} /> : null}
			</div>
		</div>
	)
}

export function NumberField({
	labelProps,
	inputProps,
	errors,
	className,
}: {
	labelProps: React.LabelHTMLAttributes<HTMLLabelElement>
	inputProps: React.InputHTMLAttributes<HTMLInputElement> & {
		key?: string
	}
	errors?: ListOfErrors
	className?: string
}) {
	const fallbackId = useId()
	const { key, ...restInputProps } = inputProps
	const id = restInputProps.id ?? fallbackId
	const errorId = errors?.length ? `${id}-error` : undefined

	return (
		<div className={className}>
			<Label htmlFor={id} {...labelProps} />
			<Input
				key={key}
				id={id}
				type="number"
				aria-invalid={errorId ? true : undefined}
				aria-describedby={errorId}
				{...restInputProps}
			/>
			<div className="min-h-[32px] px-4 pt-1 pb-3">
				{errorId ? <ErrorList id={errorId} errors={errors} /> : null}
			</div>
		</div>
	)
}

interface MediaFieldProps {
	labelProps: React.LabelHTMLAttributes<HTMLLabelElement>
	media: MediaItem[]
	inputProps: ReturnType<typeof getInputProps>
	errors?: ListOfErrors
	className?: string
	multiple?: boolean // New prop to control single vs multiple selection
}

export function MediaField({
	labelProps,
	media,
	inputProps,
	errors,
	className,
	multiple = true, // Default to multiple selection for backward compatibility
}: MediaFieldProps) {
	const fallbackId = useId()
	const id = inputProps.id ?? fallbackId
	const errorId = errors?.length ? `${id}-error` : undefined
	const input = useInputControl({
		key: inputProps.key,
		name: inputProps.name,
		formId: inputProps.form,
		initialValue: inputProps.defaultValue,
	})

	// Handle both single and multiple selection
	const selected = new Set(
		input.value
			? multiple
				? input.value.toString().split(',').filter(Boolean)
				: input.value
					? [input.value.toString()]
					: []
			: [],
	)

	const handleSelection = (itemId: string) => {
		if (multiple) {
			// Multiple selection logic
			const newSelected = new Set(selected)
			if (newSelected.has(itemId)) {
				newSelected.delete(itemId)
			} else {
				newSelected.add(itemId)
			}
			input.change(Array.from(newSelected).join(','))
		} else {
			// Single selection logic
			if (selected.has(itemId)) {
				// Deselect if already selected
				input.change('')
			} else {
				// Select this item (deselect others)
				input.change(itemId)
			}
		}
	}

	return (
		<div className={className}>
			<Label htmlFor={id} {...labelProps} />

			<div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
				{media.map((item) => (
					<MediaCard
						key={item.id}
						media={item}
						isSelected={selected.has(item.id)}
						showSelectionIndicator={true}
						onClick={() => handleSelection(item.id)}
					/>
				))}
			</div>

			{/* Hidden input for form control */}
			<input
				{...inputProps}
				type="hidden"
				id={id}
				aria-invalid={errorId ? true : undefined}
				aria-describedby={errorId}
			/>

			{/* Empty State */}
			{media.length === 0 && (
				<div className="bg-muted/50 flex min-h-[200px] items-center justify-center rounded-md">
					<div className="text-muted-foreground text-center">
						<Icon name="image" className="mx-auto size-8 opacity-50" />
						<p className="mt-2 text-sm">No media available</p>
					</div>
				</div>
			)}

			<div className="min-h-[32px] px-4 pt-1 pb-3">
				{errorId ? <ErrorList id={errorId} errors={errors} /> : null}
			</div>
		</div>
	)
}

export function MultiSelectField({
	labelProps,
	multiSelectProps,
	multiSelectOptions,
	errors,
	className,
	field,
	disabled,
	onValueChange,
}: {
	labelProps: React.LabelHTMLAttributes<HTMLLabelElement>
	multiSelectProps: {
		name: string
		placeholder?: string
		emptyText?: string
		maxSelected?: number
		hideClearAllButton?: boolean
		hidePlaceholderWhenSelected?: boolean
		creatable?: boolean
	}
	multiSelectOptions: Option[]
	errors?: ListOfErrors
	className?: string
	field: FieldMetadata<string | null> | FieldMetadata<string[] | undefined>
	disabled?: boolean
	onValueChange?: (options: Option[]) => void
}) {
	const fallbackId = useId()
	const id = multiSelectProps.name ?? fallbackId
	const errorId = errors?.length ? `${id}-error` : undefined
	const control = useInputControl(field)

	// Parse the current value (can be string array or comma-separated string) into Option array
	const selectedOptions = (() => {
		if (!control.value) return []

		// Handle array values (from array fields)
		if (Array.isArray(control.value)) {
			return multiSelectOptions.filter((option) =>
				control.value!.includes(option.value),
			)
		}

		// Handle string values (comma-separated)
		const values = control.value.split(',').filter(Boolean)
		return multiSelectOptions.filter((option) => values.includes(option.value))
	})()

	const handleChange = (options: Option[]) => {
		const values = options.map((option) => option.value)

		// Determine if this is an array field based on current value or field type
		const isArrayField =
			Array.isArray(control.value) ||
			control.value === undefined ||
			control.value === null

		if (isArrayField) {
			// For array fields, set array value
			control.change(values as any)
		} else {
			// For string fields, convert to comma-separated string
			control.change(values.join(','))
		}

		onValueChange?.(options)
	}

	return (
		<div className={className}>
			<Label htmlFor={id} {...labelProps} />
			<MultipleSelector
				value={selectedOptions}
				defaultOptions={multiSelectOptions}
				onChange={handleChange}
				disabled={disabled}
				placeholder={multiSelectProps.placeholder || 'Select options...'}
				emptyIndicator={
					<p className="text-center text-sm">
						{multiSelectProps.emptyText || 'No results found'}
					</p>
				}
				maxSelected={multiSelectProps.maxSelected}
				hideClearAllButton={multiSelectProps.hideClearAllButton}
				hidePlaceholderWhenSelected={
					multiSelectProps.hidePlaceholderWhenSelected
				}
				creatable={multiSelectProps.creatable}
				commandProps={{
					'aria-invalid': errorId ? true : undefined,
					'aria-describedby': errorId,
					filter: (value, search) => {
						const item = multiSelectOptions.find((item) => item.value === value)
						if (!item) return 0

						// Search against searchText if provided, otherwise fallback to label
						const searchableText =
							(item as any).searchText ||
							(typeof item.label === 'string' ? item.label : '')

						if (searchableText.toLowerCase().includes(search.toLowerCase()))
							return 1
						return 0
					},
				}}
			/>
			{/* Hidden inputs for form submission */}
			{Array.isArray(control.value) ? (
				// For array fields, create multiple hidden inputs
				control.value.map((value, index) => (
					<input
						key={index}
						type="hidden"
						name={multiSelectProps.name}
						value={value}
					/>
				))
			) : (
				// For string fields, single hidden input with comma-separated values
				<input
					type="hidden"
					name={multiSelectProps.name}
					value={control.value || ''}
				/>
			)}
			<div className="min-h-[32px] px-4 pt-1 pb-3">
				{errorId ? <ErrorList id={errorId} errors={errors} /> : null}
			</div>
		</div>
	)
}

interface DatePickerFieldProps {
	labelProps: React.LabelHTMLAttributes<HTMLLabelElement>
	inputProps: React.InputHTMLAttributes<HTMLInputElement> & {
		key?: string
		name: string
		form: string
	}
	buttonProps?: React.ButtonHTMLAttributes<HTMLButtonElement> & {
		key?: string
	}
	errors?: ListOfErrors
	className?: string
	disabled?: boolean
	onSelect?: (date: Date | undefined) => Date
}

export function DatePickerField({
	labelProps,
	inputProps,
	buttonProps,
	errors,
	className,
	disabled,
}: DatePickerFieldProps) {
	const { key, name, form, defaultValue, ...restInputProps } = inputProps
	const { key: buttonKey, ...restButtonProps } = buttonProps || {}
	const fallbackId = useId()
	const id = restInputProps.id ?? fallbackId
	const errorId = errors?.length ? `${id}-error` : undefined

	const control = useInputControl({
		name,
		formId: form,
		initialValue: defaultValue?.toString() || '',
	})

	return (
		<div className={className}>
			<Label htmlFor={id} {...labelProps} />
			<Popover>
				<PopoverTrigger asChild>
					<Button
						id={id}
						type="button"
						disabled={disabled || restInputProps.disabled}
						{...restButtonProps}
						variant="outline"
						className={cn(
							'w-full justify-start text-left font-normal',
							!control.value && 'text-muted-foreground',
							errorId && 'border-destructive',
							restButtonProps?.className,
						)}
						aria-invalid={errorId ? true : undefined}
						aria-describedby={errorId}
						name={name}
						form={form}
					>
						<Icon name="calendar" className="mr-2 h-4 w-4" />
						{control.value ? (
							format(new Date(control.value), 'PPP')
						) : (
							<span>Pick a date</span>
						)}
					</Button>
				</PopoverTrigger>
				<PopoverContent className="w-auto p-0" align="start">
					<Calendar
						mode="single"
						selected={control.value ? new Date(control.value) : undefined}
						onSelect={(date) => {
							if (date) {
								control.change(date.toISOString())
							} else {
								control.change('')
							}
						}}
						disabled={disabled || restInputProps.disabled}
					/>
				</PopoverContent>
			</Popover>
			<div className="min-h-[32px] px-4 pt-1 pb-3">
				{errorId ? <ErrorList id={errorId} errors={errors} /> : null}
			</div>
		</div>
	)
}

interface DateRangeFieldProps {
	labelProps: React.LabelHTMLAttributes<HTMLLabelElement>
	inputProps: {
		fromName: string
		toName: string
	}
	buttonProps?: React.ButtonHTMLAttributes<HTMLButtonElement>
	errors?: ListOfErrors
	className?: string
	disabled?: boolean
	field: {
		from: FieldMetadata<string>
		to: FieldMetadata<string>
	}
}

export function DateRangeField({
	labelProps,
	inputProps: _inputProps,
	buttonProps,
	errors,
	className,
	disabled,
	field,
}: DateRangeFieldProps) {
	const fallbackId = useId()
	const id = buttonProps?.id ?? fallbackId
	const errorId = errors?.length ? `${id}-error` : undefined

	const fromControl = useInputControl(field.from)
	const toControl = useInputControl(field.to)

	const [dateRange, setDateRange] = useState<DateRange | undefined>({
		from: fromControl.value ? new Date(fromControl.value) : undefined,
		to: toControl.value ? new Date(toControl.value) : undefined,
	})

	const isValidRange =
		dateRange?.from &&
		!isNaN(dateRange.from.getTime()) &&
		(!dateRange.to || !isNaN(dateRange.to.getTime()))

	const handleSelect = (range: DateRange | undefined) => {
		setDateRange(range)
		if (range?.from) fromControl.change(startOfDay(range.from).toISOString())
		if (range?.to) toControl.change(endOfDay(range.to).toISOString())
	}

	return (
		<div className={className}>
			<Label htmlFor={id} {...labelProps} />
			<Popover>
				<PopoverTrigger asChild>
					<Button
						id={id}
						type="button"
						disabled={disabled}
						{...buttonProps}
						variant="outline"
						className={cn(
							'w-full justify-start text-left font-normal',
							!isValidRange && 'text-muted-foreground',
							errorId && 'border-destructive',
							buttonProps?.className,
						)}
					>
						<Icon name="calendar" className="mr-2 h-4 w-4" />
						{isValidRange ? (
							dateRange.to ? (
								<>
									{format(dateRange.from as Date, 'PPP')} -{' '}
									{format(dateRange.to as Date, 'PPP')}
								</>
							) : (
								format(dateRange.from as Date, 'PPP')
							)
						) : (
							<span>Pick a date range</span>
						)}
					</Button>
				</PopoverTrigger>
				<PopoverContent className="w-auto p-0" align="start">
					<Calendar
						mode="range"
						selected={dateRange}
						onSelect={handleSelect}
						numberOfMonths={2}
						disabled={disabled}
					/>
				</PopoverContent>
			</Popover>
			<div className="min-h-[32px] px-4 pt-1 pb-3">
				{errorId ? <ErrorList id={errorId} errors={errors} /> : null}
			</div>
		</div>
	)
}
